<template>
  <form id="futuro-customer-cash" class="my-5" @submit.prevent>
    <div class="card">
      <div class="row align-items-center justify-content-between">
        <div class="card-title col-auto mb-3">
          <img
            src="~@/assets/img/ic-cash-flow.svg"
            alt="Futuro | Consultoria Financeira"
          />
          <h2>Fluxo de caixa</h2>
        </div>
        <spinner v-if="loading" class="mr-2" />
      </div>
      <div class="row mb-3 gy-3 align-items-end">
        <div class="col-md-4 col-12">
          <label for="doesAnnualPlanning" class="form-label">
            Você faz planejamento anual dos seus ganhos e gastos?
          </label>
          <select
            id="doesAnnualPlanning"
            v-model="doesAnnualPlanning"
            :disabled="isDetail"
            class="form-select"
            :class="{ 'select-disabled': isDetail }"
            @blur="preSave()"
          >
            <option value="yes">Sim</option>
            <option value="no">Não</option>
          </select>
        </div>
        <div class="col-md-4 col-12">
          <label for="doesSpendingManagement" class="form-label">
            Você faz controle dos seus gastos?
          </label>
          <select
            id="doesSpendingManagement"
            v-model="doesSpendingManagement"
            class="form-select"
            :class="{ 'select-disabled': isDetail }"
            :disabled="isDetail"
            @blur="preSave()"
          >
            <option value="yes">Sim</option>
            <option value="no">Não</option>
          </select>
        </div>
        <div class="col-md-4 col-12">
          <label for="hasYearEndBonus" class="form-label">
            Você recebe 13<sup>o</sup> salário?
          </label>
          <select
            id="hasYearEndBonus"
            v-model="hasYearEndBonus"
            :disabled="isDetail"
            class="form-select"
            :class="{ 'select-disabled': isDetail }"
            @focus="captureIncomeChangeSnapshot()"
            @blur="handleIncomeImpactingFieldBlur($event)"
          >
            <option value="yes">Sim</option>
            <option value="no">Não</option>
          </select>
        </div>
      </div>

      <div
        v-for="(category, categoryIndex) in categories"
        :key="category.key"
        class="row mb-4 pb-2 mx-2 bottom-line"
      >
        <div class="col-12">
          <div class="row align-items-center mb-3">
            <div class="col-md-6 col-12">
              <span class="category">{{ category.title }}</span>
            </div>
            <div class="col-md-4 col-12">
              <input
                v-model.lazy="category.value"
                v-money="moneyFormat"
                type="text"
                class="form-control"
                :disabled="isDetail"
                :class="{
                  'is-invalid':
                    v$.categories.$dirty &&
                    v$.categories.$each.$response.$errors[categoryIndex].value
                      .length,
                }"
                @focus="captureIncomeChangeSnapshot()"
                @blur="handleCategoryValueBlur(categoryIndex, $event)"
              />
              <div class="invalid-feedback">
                <span
                  v-if="
                    v$.categories.$dirty &&
                    v$.categories.$each.$response.$errors[categoryIndex].value
                      .length
                  "
                >
                  Digite um valor válido
                </span>
              </div>
            </div>
          </div>
          <template v-if="isInterview">
            <div
              v-for="(subCategory, subCategoryIndex) in category.subCategories"
              :key="subCategory.key"
              class="row align-items-center mb-3"
            >
              <div
                v-if="isCustomSubCategory(subCategory)"
                class="col-md-6 col-12"
              >
                <input
                  v-model.lazy="subCategory.title"
                  type="text"
                  :disabled="isDetail"
                  class="form-control"
                  @blur="preSave()"
                  @keydown.enter.prevent=""
                />
              </div>
              <div v-else class="col-md-6 col-12">
                <span>{{
                  subCategory.title === "Dívidas"
                    ? "Parcela da dívida"
                    : subCategory.title
                }}</span>
              </div>
              <div class="col-md-4 col-6 campos-texto">
                <input
                  v-model.lazy="subCategory.value"
                  v-money="moneyFormat"
                  :disabled="isDetail"
                  type="text"
                  class="form-control"
                  :class="{
                    'is-invalid':
                      v$.categories.$dirty &&
                      v$.categories.$each.$response.$data[categoryIndex]
                        .subCategories.$each.$errors[subCategoryIndex].value
                        .length,
                  }"
                  @blur="
                    handleSubCategoryValueBlur(categoryIndex, $event);
                  "
                  @focus="captureIncomeChangeSnapshot()"
                  @keydown.enter.prevent=""
                />
                <div class="invalid-feedback">
                  <span
                    v-if="
                      v$.categories.$dirty &&
                      v$.categories.$each.$response.$data[categoryIndex]
                        .subCategories.$each.$errors[subCategoryIndex].value
                        .length
                    "
                  >
                    Digite um valor válido
                  </span>
                </div>
              </div>
              <div class="col-md-2 col-6">
                <template v-if="!isDetail">
                  <button
                    v-if="isCustomSubCategory(subCategory)"
                    class="btn btn-red"
                    @click.prevent="
                      removeSubCategory(categoryIndex, subCategoryIndex)
                    "
                  >
                    Excluir
                  </button>
                </template>
              </div>
            </div>

            <div class="row align-items-center mb-3 mt-2 px-3 g-3">
              <template v-if="!isDetail">
                <button
                  class="btn btn-orange col-auto mx-1"
                  @click.prevent="addSubCategory(categoryIndex)"
                >
                  Adicionar subcategoria
                </button>
              </template>
            </div>
          </template>
        </div>
      </div>
      <template
        v-if="
          cashFlowData &&
          cashFlowData.resume_month &&
          cashFlowData.resume_year &&
          cashFlowData.current_chart
        "
      >
        <div class="row align-items-center justify-content-around mb-3">
          <span class="category">Resumo</span>
          <spinner v-if="calculating" class="mr-2" />
        </div>
        <div class="row mb-3 gy-3">
          <div class="col-md-6 col-12">
            <div class="indicador-row">
              <div class="indicador-label">
                <div class="indicador" />
                <span><b>Mensal</b></span>
              </div>
            </div>
            <div class="indicador-row">
              <div class="indicador-label">
                <div class="indicador renda-liquida" />
                <span>Renda líquida:</span>
              </div>
              <span>{{
                formatCurrency(
                  cashFlowData?.resume_month?.total_income || 0
                )
              }}</span>
            </div>
            <div class="indicador-row">
              <div class="indicador-label">
                <div class="indicador despesas-totais" />
                <span>Despesas totais:</span>
              </div>
              <span>{{
                formatCurrency(
                  cashFlowData?.resume_month?.total_expenses || 0
                )
              }}</span>
            </div>
            <div class="indicador-row ms-4">
              <div class="indicador-label">
                <div class="indicador gastos-fixos" />
                <span>Gastos fixos:</span>
              </div>
              <span>{{
                formatCurrency(
                  cashFlowData?.resume_month?.total_fixed_expenses || 0
                )
              }}</span>
            </div>
            <div class="indicador-row ms-4">
              <div class="indicador-label">
                <div class="indicador gastos-variaveis" />
                <span>Gastos variáveis:</span>
              </div>
              <span>{{
                formatCurrency(
                  cashFlowData?.resume_month?.total_variable_expenses ||
                    0
                )
              }}</span>
            </div>
            <div class="indicador-row ms-4">
              <div class="indicador-label">
                <div class="indicador investimentos" />
                <span>Investimentos:</span>
              </div>
              <span>{{
                formatCurrency(
                  cashFlowData?.resume_month?.total_investments || 0
                )
              }}</span>
            </div>
            <div class="indicador-row">
              <div class="indicador-label">
                <span>Saldo:</span>
              </div>
              <span>{{
                formatCurrency(cashFlowData?.resume_month?.balance || 0)
              }}</span>
            </div>
          </div>
          <div class="col-md-6 col-12">
            <div class="indicador-row">
              <div class="indicador-label">
                <div class="indicador" />
                <span><b>Anual</b></span>
              </div>
            </div>
            <div class="indicador-row">
              <div class="indicador-label">
                <div class="indicador renda-liquida" />
                <span>Renda líquida:</span>
              </div>
              <span>{{
                formatCurrency(
                  cashFlowData?.resume_year?.total_income || 0
                )
              }}</span>
            </div>
            <div class="indicador-row">
              <div class="indicador-label">
                <div class="indicador despesas-totais" />
                <span>Despesas totais:</span>
              </div>
              <span>{{
                formatCurrency(
                  cashFlowData?.resume_year?.total_expenses || 0
                )
              }}</span>
            </div>
            <div class="indicador-row ms-4">
              <div class="indicador-label">
                <div class="indicador gastos-fixos" />
                <span>Gastos fixos:</span>
              </div>
              <span>{{
                formatCurrency(
                  cashFlowData?.resume_year?.total_fixed_expenses || 0
                )
              }}</span>
            </div>
            <div class="indicador-row ms-4">
              <div class="indicador-label">
                <div class="indicador gastos-variaveis" />
                <span>Gastos variáveis:</span>
              </div>
              <span>{{
                formatCurrency(
                  cashFlowData?.resume_year?.total_variable_expenses || 0
                )
              }}</span>
            </div>
            <div class="indicador-row ms-4">
              <div class="indicador-label">
                <div class="indicador investimentos" />
                <span>Investimentos:</span>
              </div>
              <span>{{
                formatCurrency(
                  cashFlowData?.resume_year?.total_investments || 0
                )
              }}</span>
            </div>
            <div class="indicador-row">
              <div class="indicador-label">
                <span>Saldo:</span>
              </div>
              <span>{{
                formatCurrency(cashFlowData?.resume_year?.balance || 0)
              }}</span>
            </div>
          </div>
        </div>
              </template>
      <comments-section
        v-if="interviewSection && token"
        :interview-section="interviewSection"
        :token="token"
      />
    </div>
  </form>
</template>

<script>
import _ from "lodash";
import useVuelidate from "@vuelidate/core";
import { helpers, minLength } from "@vuelidate/validators";
import {
  floatToMoney,
  moneyToFloat,
  booleanToString,
} from "@/helpers/converters";
import { getCashFlowPayload } from "@/helpers/interview_payloads";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { VMoney } from "v-money";
import * as types from "../store/types";
import * as sharedTypes from "@/modules/shared/store/types";
import Spinner from "@/modules/shared/components/Spinner";
import CommentsSection from "./CommentsSection";
import ConfirmModal from "@/modules/shared/components/ConfirmModal";
export default {
  components: {
    Spinner,
    CommentsSection,
    ConfirmModal,
  },
  directives: {
    money: VMoney,
  },
  props: {
    interviewSection: {
      type: String,
      default: "",
    },
    isInterview: {
      type: Boolean,
      default: false,
    },
    isDetail: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    return { v$: useVuelidate({ $registerAs: props.interviewSection }) };
  },
  data() {
    return {
      doesAnnualPlanning: "",
      doesSpendingManagement: "",
      hasYearEndBonus: "",
      categories: [],
      token: "",
      loading: false,
      calculating: false,
      incomeChangeSnapshot: null,
      incomeChangeConfirmationPromise: null,
      incomeChangeWasConfirmed: false,
    };
  },
  validations() {
    return {
      doesAnnualPlanning: {},
      doesSpendingManagement: {},
      hasYearEndBonus: {},
      categories: {
        $each: helpers.forEach({
          title: {},
          category: {},
          value: {
            minLength: minLength(7),
          },
          subCategories: {
            $each: helpers.forEach({
              title: {},
              category: {},
              categorySub: {},
              value: {
                minLength: minLength(7),
              },
            }),
          },
        }),
      },
    };
  },
  computed: {
    ...mapGetters({
      cashFlowCategories: types.CASH_FLOW_CATEGORIES,
      cashFlow: types.CASH_FLOW,
      cashFlowCalculation: types.CASH_FLOW_CALCULATION,
      formatCurrency: types.FORMAT_CURRENCY,
      formatedCurrency: types.FORMATED_CURRENCY,
      currentCurrency: types.CURRENT_CURRENCY,
      debts: types.DEBTS,
    }),
    moneyFormat: function () {
      return this.formatedCurrency;
    },

    cashFlowData: function () {
      if(this.isInterview){
        return {
          ideal_chart: this.cashFlowCalculation?.ideal_chart || undefined,
          current_chart: this.cashFlowCalculation?.current_chart || undefined,
          resume_month:
            this.cashFlowCalculation?.resume_month || undefined,
          resume_year: 
            this.cashFlowCalculation?.resume_year || undefined,
        }
      }
      return {
        ideal_chart: this.cashFlowCalculation?.ideal_chart || undefined,
        current_chart: this.cashFlowCalculation?.pre_chart || undefined,
        resume_month:
          this.cashFlowCalculation?.pre_resume_month || undefined,
        resume_year: 
          this.cashFlowCalculation?.pre_resume_year || undefined,
      };
    },

    actualOfFixed: function () {
      return this.cashFlowData.current_chart?.fixed_expenses || 0;
    },
    actualOfVariable: function () {
      return this.cashFlowData.current_chart?.variable_expenses || 0;
    },
    actualOfInvestiments: function () {
      return this.cashFlowData.current_chart?.investments || 0;
    },
    idealOfFixed: function () {
      return this.cashFlowData.ideal_chart?.fixed_expenses || 0;
    },
    idealOfVariables: function () {
      return this.cashFlowData.ideal_chart?.variable_expenses || 0;
    },
    idealOfInvestiments: function () {
      return this.cashFlowData.ideal_chart?.investments || 0;
    },
    balanceValue: function () {
      return this.cashFlowData.resume_month?.balance || 0;
    },
    totalIncome: function () {
      return this.cashFlowData.resume_month?.total_income || 0;
    },
    calculateIdealOfFixed: function () {
      const ideal = this.idealOfFixed / 100;
      const total = this.totalIncome;
      const fixed = total * ideal;
      return fixed || 0;
    },
    calculateIdealOfVariable: function () {
      const ideal = this.idealOfVariables / 100;
      const total = this.totalIncome;
      const fixed = total * ideal;
      return fixed || 0;
    },
    calculateIdealOfInvestiments: function () {
      const ideal = this.idealOfInvestiments / 100;
      const total = this.totalIncome;
      const fixed = total * ideal;
      return fixed || 0;
    },
  },
  watch: {
    "debts.debts": {
      handler(newDebts) {
        // Sum the monthly installment amount ("Valor da parcela") instead of the total debt value
        const totalMonthlyInstallment = (newDebts || []).reduce((sum, debt) => {
          if (!debt) return sum;
          const raw = debt.amount_paid ?? debt.amountPaid ?? 0; // support both payload and local naming just in case
          const cleanedValue =
            typeof raw === "number"
              ? raw
              : parseFloat(
                  raw
                    .toString()
                    .replace(/[^\d,-]/g, "")
                    .replace(",", ".")
                ) || 0;
          return sum + cleanedValue;
        }, 0);

        const payload = getCashFlowPayload(this);

        // Find 'gasto_fixo' in the categories array and get its index
        const fixedExpensesIndex = payload.categories.findIndex(
          (category) => category.category === "gasto_fixo"
        );

        // If 'gasto_fixo' category is not found, exit
        if (
          fixedExpensesIndex === -1 ||
          !payload.categories[fixedExpensesIndex].sub_categories
        ) {
          return;
        }

        const fixedExpenses = payload.categories[fixedExpensesIndex];

        // Find 'divida' in the sub_categories array of 'gasto_fixo'
        const debts = fixedExpenses.sub_categories.find(
          (subCategory) => subCategory.category_sub === "divida"
        );

        if (debts) {
          debts.value = totalMonthlyInstallment; // Set monthly installment as the fixed expense for debts
        }

        this.fillCategories(payload.categories);
        this.mutateCashFlow(payload);

        // Use the dynamically found index for sumSubCategoryValues
        this.sumSubCategoryValues(fixedExpensesIndex);
      },
      deep: true,
    },
  },
  async mounted() {
    this.token = this.$route.params.customerId;
    await this.fetchAll();
  },
  methods: {
    ...mapActions({
      getCategories: types.GET_CASH_FLOW_CATEGORIES,
      getCashFlow: types.GET_CASH_FLOW,
      autoSaveCashFlow: types.AUTO_SAVE_CASH_FLOW,
      calculateCashFlow: types.CALCULATE_CASH_FLOW,
      recalculateProposal: types.CALCULATE_PROPOSAL,
    }),
    ...mapMutations({
      mutateCashFlow: types.MUTATE_CASH_FLOW,
      changeLoadingState: types.MUTATE_INTERVIEW_CASH_FLOW_LOADING,
    }),
    async fetchCategories() {
      this.loading = true;
      this.changeLoadingState(true);
      try {
        await this.getCategories();
        try {
          const response = await this.getCashFlow(
            this.$route.params.customerId
          );
          const {
            does_anual_planning,
            does_spending_management,
            has_year_end_bonus,
            categories,
          } = response.data;
          this.doesAnnualPlanning = booleanToString(does_anual_planning);
          this.doesSpendingManagement = booleanToString(
            does_spending_management
          );
          this.hasYearEndBonus = booleanToString(has_year_end_bonus);
          this.fillCategories(categories);
          const payload = getCashFlowPayload(this);
          this.mutateCashFlow(payload);
        } catch (error) {
          this.$vfm.show("message", {
            type: "error",
            messages: [
              "Não foi possível obter os objetivos do cliente.",
              "Entre em contato com o time de TI antes de prosseguir com a operação.",
            ],
          });
        } finally {
          this.loading = false;
          this.changeLoadingState(false);
        }
      } catch (error) {
        this.$vfm.show("message", {
          type: "error",
          messages: [
            "Não foi possível obter o fluxo de caixa do cliente.",
            "Entre em contato com o time de TI antes de prosseguir com a operação.",
          ],
        });
      } finally {
        this.loading = false;
        this.changeLoadingState(false);
      }
    },

    async fetchAll() {
      this.loading = true;
      try {
        await Promise.all([this.fetchCategories(), this.calculate()]);
      } catch (error) {
      } finally {
        this.loading = false;
      }
    },

    addSubCategoryData(data) {
      const category = this.categories[data.categoryIndex];

      if (!category.subCategories) {
        category.subCategories = [];
      }

      const existingSubCategoryIndex = category.subCategories.findIndex(
        (sub) => sub.title === data.title
      );

      if (existingSubCategoryIndex !== -1) {
        category.subCategories.splice(existingSubCategoryIndex, 1);
      }

      category.subCategories.push({
        title: data.title,
        category: category.category,
        categorySub: `sub_category_${category.subCategories.length + 1}`,
        value: data.value,
      });

      this.sumSubCategoryValues(data.categoryIndex);
      this.preSave();
    },

    addSubCategory(index) {
      const category = this.categories[index];
      category.subCategories.push({
        title: "",
        category: category.category,
        categorySub: `sub_category_${category.subCategories.length + 1}`,
        value: "",
      });
      this.preSave();
    },
    removeSubCategory(categoryIndex, subCategoryIndex) {
      this.categories[categoryIndex].subCategories.splice(subCategoryIndex, 1);
      this.sumSubCategoryValues(categoryIndex);
      this.preSave();
    },
    removeSubCategoryData(data) {
      const existingSubCategoryIndex = this.categories[
        data.categoryIndex
      ].subCategories.findIndex((sub) => sub.title === data.title);

      this.categories[data.categoryIndex].subCategories.splice(
        existingSubCategoryIndex,
        1
      );

      this.sumSubCategoryValues(data.categoryIndex);
      this.preSave();
    },
    captureIncomeChangeSnapshot() {
      if (
        !this.isInterview ||
        this.incomeChangeWasConfirmed ||
        this.incomeChangeSnapshot
      ) return;

      this.incomeChangeSnapshot = this.getCurrentIncomeSnapshot();
    },
    restoreIncomeChangeSnapshot() {
      if (!this.incomeChangeSnapshot) return;

      const snapshot = JSON.parse(this.incomeChangeSnapshot);
      this.doesAnnualPlanning = snapshot.doesAnnualPlanning;
      this.doesSpendingManagement = snapshot.doesSpendingManagement;
      this.hasYearEndBonus = snapshot.hasYearEndBonus;
      this.categories = snapshot.categories;
      this.incomeChangeSnapshot = null;
    },
    clearIncomeChangeSnapshot() {
      this.incomeChangeSnapshot = null;
    },
    isIncomeCategory(categoryIndex) {
      return this.categories[categoryIndex]?.category === "renda";
    },
    handleCategoryValueBlur(categoryIndex, event) {
      this.confirmIncomeChangeBeforePreSave(
        this.isIncomeCategory(categoryIndex),
        event
      );
    },
    handleSubCategoryValueBlur(categoryIndex, event) {
      this.sumSubCategoryValues(categoryIndex);
      this.confirmIncomeChangeBeforePreSave(
        this.isIncomeCategory(categoryIndex),
        event
      );
    },
    handleIncomeImpactingFieldBlur(event) {
      this.confirmIncomeChangeBeforePreSave(true, event);
    },
    getCurrentIncomeSnapshot() {
      return JSON.stringify({
        doesAnnualPlanning: this.doesAnnualPlanning,
        doesSpendingManagement: this.doesSpendingManagement,
        hasYearEndBonus: this.hasYearEndBonus,
        categories: this.categories,
      });
    },
    hasPendingIncomeChangeConfirmation() {
      if (!this.incomeChangeSnapshot) return false;

      return this.getCurrentIncomeSnapshot() !== this.incomeChangeSnapshot;
    },
    incomeSnapshotHasIncomeValue(snapshot) {
      if (!snapshot) return false;

      const parsedSnapshot = typeof snapshot === "string"
        ? JSON.parse(snapshot)
        : snapshot;
      const incomeCategory = parsedSnapshot.categories?.find(
        (category) => category.category === "renda"
      );

      if (!incomeCategory) return false;

      const categoryValue = moneyToFloat(incomeCategory.value || 0);
      const subCategoriesValue = _.sumBy(
        incomeCategory.subCategories || [],
        (subCategory) => moneyToFloat(subCategory.value || 0)
      );

      return categoryValue > 0 || subCategoriesValue > 0;
    },
    shouldShowIncomeChangeConfirmation() {
      return this.incomeSnapshotHasIncomeValue(this.incomeChangeSnapshot);
    },
    isContractGenerationTrigger(event) {
      const relatedTarget = event?.relatedTarget;

      return typeof relatedTarget?.closest === "function"
        && relatedTarget.closest("[data-generate-contract-trigger='true']");
    },
    shouldDeferIncomeChangeConfirmation(event) {
      return this.isContractGenerationTrigger(event)
        || this.loading
        || this.calculating;
    },
    showIncomeChangeConfirmation() {
      if (this.incomeChangeConfirmationPromise) {
        return this.incomeChangeConfirmationPromise;
      }

      this.incomeChangeConfirmationPromise = new Promise((resolve) => {
        const finalize = (value) => {
          this.incomeChangeConfirmationPromise = null;
          resolve(value);
        };

        this.$vfm.show(
          {
            component: ConfirmModal,
          },
          {
            messages: [
              "Alterando a renda do cliente, influenciara em toda a precificacao do Plano Financeiro. Tem certeza que deseja alterar os valores?",
            ],
            handleConfirm: () => {
              this.incomeChangeWasConfirmed = true;
              this.clearIncomeChangeSnapshot();
              this.preSave();
              finalize(true);
            },
            handleCancel: () => {
              this.restoreIncomeChangeSnapshot();
              finalize(false);
            },
          }
        );
      });

      return this.incomeChangeConfirmationPromise;
    },
    confirmPendingIncomeChange() {
      if (this.incomeChangeWasConfirmed) {
        this.clearIncomeChangeSnapshot();
        return Promise.resolve(true);
      }

      if (!this.incomeChangeSnapshot) {
        return Promise.resolve(true);
      }

      if (!this.hasPendingIncomeChangeConfirmation()) {
        this.clearIncomeChangeSnapshot();
        return Promise.resolve(true);
      }

      if (!this.shouldShowIncomeChangeConfirmation()) {
        this.clearIncomeChangeSnapshot();
        this.preSave();
        return Promise.resolve(true);
      }

      return this.showIncomeChangeConfirmation();
    },
    confirmIncomeChangeBeforePreSave(shouldConfirm, event) {
      if (this.incomeChangeWasConfirmed) {
        this.clearIncomeChangeSnapshot();
        this.preSave();
        return;
      }

      if (!shouldConfirm || !this.incomeChangeSnapshot) {
        this.clearIncomeChangeSnapshot();
        this.preSave();
        return;
      }

      if (!this.hasPendingIncomeChangeConfirmation()) {
        this.clearIncomeChangeSnapshot();
        this.preSave();
        return;
      }

      if (!this.shouldShowIncomeChangeConfirmation()) {
        this.clearIncomeChangeSnapshot();
        this.preSave();
        return;
      }

      if (this.shouldDeferIncomeChangeConfirmation(event)) return;

      this.showIncomeChangeConfirmation();
    },
    preSave() {
      const payload = getCashFlowPayload(this);
      const currentPayload = JSON.stringify(payload);
      if (this.tempPayload !== currentPayload) {
        this.tempPayload = currentPayload;
        this.mutateCashFlow(payload);
        this.autoSave(payload);
      }
    },
    autoSave(payload) {
      if (!this.loading) {
        this.loading = true;
        this.autoSaveCashFlow(payload)
          .then((response) => {
            this.calculate();
          })
          .catch((error) => {
            this.$vfm.show("message", {
              type: "error",
              messages: [
                "Não foi possível salvar automaticamente o fluxo de caixa do cliente.",
                "Entre em contato com o time de TI antes de prosseguir com a operação.",
              ],
            });
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
    async calculate() {
      this.calculating = true;
      if (this.isInterview) {
        await this.recalculateProposal(this.token);
      }
      await this.calculateCashFlow(this.token)
        .catch((error) => {
          this.$vfm.show("message", {
            type: "error",
            messages: [
              "Não foi possível obter os gráficos do fluxo de caixa do cliente.",
              "Entre em contato com o time de TI antes de prosseguir com a operação.",
            ],
          });
        })
        .finally(() => {
          this.calculating = false;
        });
    },
    fillCategories(categories) {
      this.categories = [];
      if (!categories?.length) {
        if (this.cashFlowCategories) {
          this.cashFlowCategories.forEach((cashFlowCategory) => {
            const newCategory = {
              title: cashFlowCategory.title,
              category: cashFlowCategory.key,
              value: "",
              subCategories: [],
            };
            if (cashFlowCategory.sub_categories) {
              cashFlowCategory.sub_categories.forEach((cashFlowSubCategory) => {
                newCategory.subCategories.push({
                  title: cashFlowSubCategory.title,
                  category: cashFlowCategory.key,
                  categorySub: cashFlowSubCategory.key,
                  value: "",
                });
              });
            }
            this.categories.push(newCategory);
          });
        }
      } else {
        categories.forEach((cashFlowCategory) => {
          const newCategory = {
            title: cashFlowCategory.title,
            category: cashFlowCategory.category,
            value: floatToMoney(cashFlowCategory.value),
            subCategories: _.map(
              cashFlowCategory.sub_categories,
              (cashFlowSubCategory) => {
                return {
                  title: cashFlowSubCategory.title,
                  category: cashFlowSubCategory.category,
                  categorySub: cashFlowSubCategory.category_sub,
                  value: floatToMoney(cashFlowSubCategory.value),
                };
              }
            ),
          };
          this.categories.push(newCategory);
        });
      }
    },
    isCustomSubCategory(subCategory) {
      return subCategory.categorySub?.startsWith("sub_category_");
    },
    sumSubCategoryValues(index) {
      const category = this.categories[index];
      if (category.category === "renda") {
        let value_salario_bruto = 0;
        let value_salario_liquido = 0;
        let sum = _.sumBy(category.subCategories, (subCategory) => {
          const value = moneyToFloat(subCategory.value);
          if (subCategory.categorySub === "salario_bruto") {
            value_salario_bruto = value;
          } else if (subCategory.categorySub === "salario_liquido") {
            value_salario_liquido = value;
          }
          return value;
        });
        if (value_salario_bruto && value_salario_liquido) {
          sum -= value_salario_bruto;
        }
        category.value = floatToMoney(sum);
      } else {
        category.value = floatToMoney(
          _.sumBy(category.subCategories, (subCategory) =>
            moneyToFloat(subCategory.value)
          )
        );
      }
    },
  },
};
</script>

<style>
@media (max-width: 768px) {
  .campos-texto {
    width: 100% !important;
  }
}
</style>

<style lang="scss" scoped>
.category {
  font-size: 22px;
  font-weight: 600;
}

.statistics-section {
  border-top: 1px solid rgba(48, 59, 82, 0.8);
}

.bottom-line {
  border-bottom: 1px solid rgba(48, 59, 82, 0.8);
}

.indicador-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.indicador-label {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
}

.indicador {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  margin-right: 10px;

  &.renda-liquida {
    background-color: #03c276;
  }

  &.gastos-fixos {
    background-color: #b10cf9;
  }

  &.gastos-variaveis {
    background-color: #ff64be;
  }

  &.investimentos {
    background-color: #43c5ff;
  }

  &.despesas-totais {
    background-color: #ff4d4f;
    // background-color: #e6654c;
  }
}

.danger {
  color: red;
}
</style>
